import { useCallback, useEffect, useMemo } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { debounce } from 'lodash';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { TeamMember } from '@dynamika-shared/types/teamMember';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useTeamMembersListSlice } from '@restore/teamMembersList/teamMembersList.slice';
import { useUsersListSlice } from '@restore/usersList/usersList.slice';
import { useClinicalTrialTeamPageSlice } from '@restore/ClinicalTrialTeamPage/ClinicalTrialTeamPage.slice';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { useClinicalTrialTeamFilters } from './hooks/useClinicalTrialTeamFilters';
import { usePaginationAndSort } from '@restore/helpers';
import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { NestedFilter } from '@components/NestedFilter/NestedFilter';
import { AddEditMember } from './components/AddEditMember/AddEditMember';
import { ConfirmationMembers } from './components/Members/ConfirmationMembers';
import { uiControlSchemas } from '../uiControlSchemaByStatus/schemas';
import { TeamMembersProvider } from './hooks/useTeamMembers';

export type MemberOptionType = 'edit' | 'suspend' | 'delete';

export const ClinicalTrialMembersPage = () => {
  const [team, loading, fetchTeam] = useTeamMembersListSlice(
    (state, actions) => [
      state.data,
      state.loading,
      actions.fetchTeamMembersList,
    ]
  );
  const id = useParams()?.id ?? '';

  const clinicalTrial = useClinicalTrialSlice(state => state.data);
  const [institutions, fetchUsersInstitution] = useUsersListSlice(
    (state, actions) => [state.institutions, actions.fetchUsersInstitution]
  );
  const [
    searchValue,
    uiControlSchema,
    addEditMemberModalOpen,
    currentModal,
    currentTeamMember,
    setCurrentTeamMember,
    setCurrentModal,
    setUiControlSchema,
    setAddEditMemberModalOpen,
    setSearchValue,
    confirmActionWithMember,
    resetSearchValue,
  ] = useClinicalTrialTeamPageSlice((state, actions) => [
    state.searchValue,
    state.uiControlSchema,
    state.addEditMemberModalOpen,
    state.currentModal,
    state.currentTeamMember,
    actions.setCurrentTeamMember,
    actions.setCurrentModal,
    actions.setUiControlSchema,
    actions.setAddEditMemberModalOpen,
    actions.setSearchValue,
    actions.confirmActionWithMember,
    actions.resetSearchValue,
  ]);

  const {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
    args,
    fetchInstitutions,
  } = useClinicalTrialTeamFilters({ id });

  const {
    sortBy,
    currentPage,
    currentPageSize,
    setSortBy,
    resetListStateByName,
  } = usePaginationAndSort('teamMembers');

  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [clinicalTrial?.name, 'clinicalTrialGeneralInformation'],
      ['Team', 'clinicalTrialTeam'],
    ],
    [clinicalTrial?.id]
  );

  const sort = sortBy ?? undefined;

  const onSort: OnSortColumnType = (name, sortState) => {
    const newSort = sortState ? `${name},${sortState}` : undefined;
    setSortBy({ listName: 'teamMembers', newSortBy: newSort });
  };

  const delayedSearch = useMemo(
    () =>
      debounce((value: string) => {
        fetchTeam({
          clinicalTrialId: id,
          args: {
            size: currentPageSize,
            page: currentPage,
            sort,
            search: value,
            ...args,
          },
        });
      }, 500),
    [currentPage, currentPageSize, id, sort, args]
  );

  const updateTeam = useCallback(() => {
    if (id) {
      fetchTeam({
        clinicalTrialId: id,
        args: {
          sort,
          size: currentPageSize,
          page: currentPage,
          search: searchValue,
        },
      });
      fetchInstitutions?.(id);
    }
  }, [
    currentPage,
    currentPageSize,
    fetchInstitutions,
    fetchTeam,
    id,
    searchValue,
    sort,
  ]);

  const dropDownClickHandler = useMemo(
    () => ({
      edit: (member: TeamMember) => {
        setCurrentTeamMember(member);
        setAddEditMemberModalOpen(true);
      },
      suspend: (member: TeamMember) => {
        console.log(member);
      },
      delete: (member: TeamMember) => {
        setCurrentTeamMember(member);
        setCurrentModal('DELETE_MEMBER');
      },
    }),
    []
  );

  const dropDownOnClick = useCallback(
    (option: MemberOptionType, member: TeamMember) => {
      dropDownClickHandler[option](member);
    },
    [dropDownClickHandler]
  );

  useEffect(() => {
    if (id) {
      fetchTeam({
        clinicalTrialId: id,
        args: {
          sort,
          size: currentPageSize,
          page: currentPage,
          search: searchValue,
          organizationId: args.institution,
          siteCode: args.siteCode,
          role: args.role,
        },
      });
    }
  }, [currentPage, currentPageSize, id, sort, args]);

  useEffect(() => {
    if (clinicalTrial?.status) {
      const schema = uiControlSchemas[clinicalTrial?.status].team;
      setUiControlSchema(schema);
    }
  }, [clinicalTrial?.status]);

  useEffect(() => {
    fetchUsersInstitution(undefined);
    return () => {
      resetSearchValue();
      resetListStateByName({ listName: 'teamMembers' });
    };
  }, []);

  const pagination = useMakePagination('teamMembers', !!team?.content?.length);

  const contextValue = {
    teamMemberData: team?.content || [],
    totalItems: team?.totalElements ?? 0,
    loading,
    onSort,
    sortState: sortBy,
    pagination,
    onClick: dropDownOnClick,
    uiControlSchema,
    institutions,
  };

  return (
    <TeamMembersProvider value={contextValue}>
      <div>
        <SubPageHeader
          title='Team Members'
          action={() => {
            setAddEditMemberModalOpen(true);
          }}
          showButton
          buttonText={'Member'}
        >
          <NestedFilter
            onClick={filterOnClick}
            items={filters}
            subFilters={subFilters}
            allFilters={allFilters}
            closeTag={toggleActiveFilter}
            toggleSelectedFilter={(name, value) =>
              toggleSelectedFilter({ name, value })
            }
            selectedFilters={selectedFilters}
            overlayClassName={'w-[22.4rem]'}
          />
          <Input
            value={searchValue}
            onChange={e => {
              setSearchValue(e.target.value);
              delayedSearch(e.target.value);
            }}
            className='!mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2'
            placeholder='Search by name or email'
            suffix={
              <SearchSuffix
                disabled={!searchValue}
                action={() => {
                  setSearchValue(undefined);
                  delayedSearch('');
                }}
              />
            }
          />
        </SubPageHeader>
        <Outlet />

        <AddEditMember
          open={addEditMemberModalOpen}
          close={() => {
            setAddEditMemberModalOpen(false);
            setCurrentTeamMember(undefined);
          }}
          currentTeamMember={currentTeamMember}
          editMode={!!currentTeamMember}
          updateTeam={updateTeam}
        />

        {currentModal && (
          <ConfirmationMembers
            modalType={currentModal}
            onCancel={() => {
              setCurrentTeamMember(undefined);
              setCurrentModal(undefined);
            }}
            onOk={() => confirmActionWithMember(undefined)}
          />
        )}
      </div>
    </TeamMembersProvider>
  );
};
